import React, { FC, useState, useCallback } from 'react';
import { TopUpProduct } from '@dreamai/types';
import './top-up-modal.scss';

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (topUp: TopUpProduct, quantity: number) => void;
  topUp: TopUpProduct | null;
  cardInfo: { last4: string; brand: string } | null;
  isSubmitting: boolean;
  error?: string | null;
}

const BRAND_ICONS: Record<string, string> = {
  visa: '/card-icons/visa.svg',
  mastercard: '/card-icons/mastercard.svg',
  'american express': '/card-icons/amex.svg',
  amex: '/card-icons/amex.svg',
  maestro: '/card-icons/maestro.svg',
  jcb: '/card-icons/jcb.svg',
  discover: '/card-icons/discover.svg',
};

const GENERIC_CARD_ICON = '/card-icons/generic.svg';

function formatPrice(amount: number, currency: string): string {
  const symbol =
    currency.toUpperCase() === 'GBP'
      ? '£'
      : currency.toUpperCase() === 'USD'
        ? '$'
        : currency.toUpperCase() === 'EUR'
          ? '€'
          : `${currency} `;
  const withTax = amount * 1.2;
  const rounded = Math.round(withTax);
  return `${symbol}${rounded}`;
}

const TopUpModal: FC<TopUpModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  topUp,
  cardInfo,
  isSubmitting,
  error,
}) => {
  const [quantity, setQuantity] = useState(1);

  const handleClose = useCallback(() => {
    setQuantity(1);
    onClose();
  }, [onClose]);

  if (!isOpen || !topUp) return null;

  const isAi = topUp.creditType === 'ai_time';
  const step = isAi ? 5 : 1;
  const totalHours = topUp.hoursPerUnit * step * quantity;
  const totalPrice = topUp.unitAmount * step * quantity;
  const borderColor = isAi ? '#ffd601' : '#489fd9';

  const canDecrease = quantity > 1;

  return (
    <div className='topup-modal-overlay' onClick={handleClose}>
      <div className='topup-modal' onClick={(e) => e.stopPropagation()}>
        <div className='topup-modal-header'>
          <p className='topup-modal-title'>
            Top-up {isAi ? 'AI' : 'Human'} time
          </p>
          <button className='topup-close-button' onClick={handleClose}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='topup-modal-body'>
          <div className='topup-pill-row' style={{ borderColor }}>
            <div className={`topup-number ${isAi ? 'ai' : 'human'}`}>
              {totalHours}
            </div>
            <div className='topup-label-col'>
              <span className='topup-unit'>
                {totalHours === 1 ? 'Hour' : 'Hours'}
              </span>
              <span className='topup-type'>{isAi ? 'AI' : 'Human'}</span>
            </div>
            <div className='topup-quantity-controls'>
              <button
                className='topup-qty-btn'
                disabled={!canDecrease}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='12' cy='12' r='10' />
                  <line x1='8' y1='12' x2='16' y2='12' />
                </svg>
              </button>
              <span className='topup-price-value'>
                {formatPrice(totalPrice, topUp.currency)}
              </span>
              <button
                className='topup-qty-btn'
                onClick={() => setQuantity((q) => q + 1)}
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='12' cy='12' r='10' />
                  <line x1='12' y1='8' x2='12' y2='16' />
                  <line x1='8' y1='12' x2='16' y2='12' />
                </svg>
              </button>
            </div>
          </div>

          <p className='topup-description'>
            We'll top-up your account with {totalHours}{' '}
            {totalHours === 1 ? 'hour' : 'hours'} of {isAi ? 'AI' : 'Human'}{' '}
            time.
          </p>

          {cardInfo && (
            <div className='topup-card-row'>
              <img
                className='card-brand-icon'
                src={
                  BRAND_ICONS[cardInfo.brand.toLowerCase()] || GENERIC_CARD_ICON
                }
                alt={cardInfo.brand}
              />
              <span className='card-ending'>Card ending {cardInfo.last4}</span>
            </div>
          )}

          {error && <p className='topup-modal-error'>{error}</p>}

          <button
            className='topup-confirm-btn'
            onClick={() => onConfirm(topUp, step * quantity)}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Processing...'
              : `Top up my account for ${formatPrice(totalPrice, topUp.currency)}`}
          </button>

          <p className='topup-legal'>
            By purchasing this top-up, you agree to immediate delivery and waive
            your 14-day cancellation right. Credits are non-refundable once
            used.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;
